import React, { useEffect, useMemo, useState } from "react";

import config from "../../../lib/config";
import { siteApi } from "../../../lib/api";
import { launchProviderLobby } from "../../../lib/gameLaunch";
import { StyledCasinoLobbyPage } from "./StyledCasinoLobbyPage";

const SORT_OPTIONS = [
  { value: "popular", label: "인기순" },
  { value: "name", label: "이름순" },
];

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeKoreanTitle = (value = "") =>
  value
    .replace(/\bBTI\b/gi, "비티아이")
    .replace(/\bBT1\b/gi, "비티원")
    .replace(/\bSABA\b/gi, "사바")
    .replace(/\bSA(?=게이밍|카지노|스포츠|\s|$)/gi, "에스에이")
    .replace(/\s+/g, " ")
    .trim();

const getVendorTitle = (vendor) =>
  normalizeKoreanTitle(
    vendor?.vendor_name_kr ||
      vendor?.vendor_name_ko ||
      vendor?.display_name ||
      vendor?.vendor_name ||
      "카지노게임"
  );

const uniqueUrls = (urls) => Array.from(new Set(urls.filter(Boolean)));

const getVendorImages = (vendor) =>
  uniqueUrls([
    resolveAssetUrl(vendor?.image),
    resolveAssetUrl(vendor?.pc_image),
    resolveAssetUrl(vendor?.mobile_image),
    resolveAssetUrl(vendor?.thumbnail),
    resolveAssetUrl(vendor?.vendor_list?.thumbnails),
  ]);

const CasinoVendorCard = ({ vendor, index, onLaunch }) => {
  const imageCandidates = useMemo(() => getVendorImages(vendor), [vendor]);
  const [imageIndex, setImageIndex] = useState(0);
  const image = imageCandidates[imageIndex] || "";
  const title = getVendorTitle(vendor);

  return (
    <button
      type="button"
      className="casino-lobby-card-link"
      onClick={() => onLaunch(vendor, title)}
      aria-label={`${title} 실행`}
    >
      <article className={`casino-lobby-card ${image ? "has-image" : "no-image"}`}>
        {image ? (
          <img
            src={image}
            alt={title}
            loading={index < 8 ? "eager" : "lazy"}
            onError={() => setImageIndex((current) => current + 1)}
          />
        ) : null}
        <div className="casino-lobby-card-title">
          <strong>{title}</strong>
        </div>
        <div className="casino-lobby-card-hover">
          <span className="casino-lobby-play" aria-hidden="true" />
          <strong>{title}</strong>
        </div>
      </article>
    </button>
  );
};

const LiveCasino = () => {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("popular");

  useEffect(() => {
    let isMounted = true;

    const loadVendors = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await siteApi.getGameVendors({
          game_type: "live-casino",
          order_by_name: "sequence",
          order_by_type: "ASC",
          limit: "ALL",
        });

        if (!isMounted) return;

        if (response.success) {
          setVendors(getRows(response));
          return;
        }

        setError(response.error || "카지노 목록을 불러오지 못했습니다.");
      } catch {
        if (isMounted) setError("카지노 목록을 불러오지 못했습니다.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredVendors = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const rows = keyword
      ? vendors.filter((vendor) => {
          const title = getVendorTitle(vendor).toLowerCase();
          const vendorName = String(vendor?.vendor_name || "").toLowerCase();
          return title.includes(keyword) || vendorName.includes(keyword);
        })
      : [...vendors];

    if (sortBy === "name") {
      return rows.sort((a, b) => getVendorTitle(a).localeCompare(getVendorTitle(b), "ko"));
    }

    return rows;
  }, [search, sortBy, vendors]);

  const handleLaunch = (vendor, title) => {
    launchProviderLobby(vendor, { label: title });
  };

  return (
    <StyledCasinoLobbyPage>
      <div className="casino-lobby-shell">
        <div className="casino-lobby-search-row">
          <label className="casino-lobby-search-box">
            <span className="casino-search-icon" aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder="카지노게임 검색"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        <div className="casino-lobby-toolbar">
          <div className="casino-lobby-title">
            <h1>카지노게임</h1>
            <span>{isLoading ? "불러오는 중" : `${filteredVendors.length}개 게임사`}</span>
          </div>

          <label className="casino-sort-control">
            <span>정렬</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <div className="casino-lobby-state error">{error}</div> : null}

        <div className="casino-lobby-grid" aria-busy={isLoading}>
          {isLoading
            ? Array.from({ length: 12 }).map((_, index) => (
                <span className="casino-lobby-skeleton" key={`casino-skeleton-${index}`} />
              ))
            : filteredVendors.map((vendor, index) => (
                <CasinoVendorCard
                  key={vendor?.id || vendor?.vendor_name || index}
                  vendor={vendor}
                  index={index}
                  onLaunch={handleLaunch}
                />
              ))}
        </div>

        {!isLoading && !error && filteredVendors.length === 0 ? (
          <div className="casino-lobby-state">표시할 카지노게임이 없습니다.</div>
        ) : null}
      </div>
    </StyledCasinoLobbyPage>
  );
};

export default LiveCasino;
